<template>
  <v-dialog v-model="dialog" max-width="980" persistent>
    <v-card class="pmc-card">
      <v-card-title class="pmc-card__title">
        <div>
          <div class="text-h6">Checklist de promoción</div>
          <div class="caption grey--text text--darken-1">
            {{ candidate?.FullName || "Sin candidato" }}
          </div>
        </div>
        <v-spacer />
        <v-chip small color="teal" text-color="white">
          {{ completedCount }}/{{ totalCount }} completados
        </v-chip>
      </v-card-title>

      <v-card-text>
        <div v-if="loadingData" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <v-row v-else>
          <v-col cols="12" md="7">
            <div class="subtitle-2 font-weight-bold mb-3">Checks definidos</div>
            <div v-if="items.length === 0" class="grey--text text--darken-1">
              No hay checks configurados en la definición 1688.
            </div>
            <div v-else class="pmc-list">
              <div
                v-for="item in items"
                :key="item.DedValue"
                class="pmc-item"
              >
                <v-checkbox
                  v-model="item.IsChecked"
                  :label="item.DedDescription"
                  hide-details
                  class="mt-0 pt-0"
                />
                <v-textarea
                  v-model="item.Observation"
                  outlined
                  dense
                  rows="2"
                  class="mt-2"
                  label="Observación"
                />
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="5">
            <div class="subtitle-2 font-weight-bold mb-3">Adjuntos del candidato</div>
            <v-file-input
              v-model="selectedFiles"
              multiple
              dense
              outlined
              show-size
              counter
              small-chips
              prepend-icon="mdi-paperclip"
              label="Agregar archivos"
            />
            <div class="d-flex justify-end mb-3">
              <v-btn
                small
                color="primary"
                :disabled="!selectedFiles.length"
                :loading="uploadingFiles"
                @click="uploadFiles"
              >
                <v-icon small left>mdi-upload</v-icon>
                Subir
              </v-btn>
            </div>

            <v-list dense two-line class="pmc-files">
              <v-list-item v-if="files.length === 0">
                <v-list-item-content>
                  <v-list-item-title class="grey--text text--darken-1">
                    Sin adjuntos registrados
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-for="file in files" :key="file.CpfID">
                <v-list-item-content>
                  <v-list-item-title>{{ file.OriginalName || file.FileName }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ formatDate(file.SecCreate) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon small @click="downloadFile(file)">
                    <v-icon small color="indigo">mdi-download</v-icon>
                  </v-btn>
                </v-list-item-action>
                <v-list-item-action>
                  <v-btn icon small @click="removeFile(file)">
                    <v-icon small color="error">mdi-delete-outline</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="closeDialog">Cerrar</v-btn>
        <v-btn color="primary" :loading="saving" @click="saveChecklist">
          Guardar checklist
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import GenUploadFiles from "@/services/General/GenUploadFiles";
import HmnCandidateProcessService from "@/services/HumanResource/RecruitmentProcess/HmnCandidateProcessService";

export default {
  name: "HmnPromotionChecklistDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    candidate: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loadingData: false,
      saving: false,
      uploadingFiles: false,
      items: [],
      files: [],
      selectedFiles: [],
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    totalCount() {
      return this.items.length;
    },
    completedCount() {
      return this.items.filter((item) => item.IsChecked).length;
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        this.loadData();
      }
    },
  },
  methods: {
    async loadData() {
      if (!this.candidate?.CprID) {
        this.items = [];
        this.files = [];
        return;
      }

      this.loadingData = true;
      try {
        const [checklistRes, filesRes] = await Promise.all([
          HmnCandidateProcessService.promotionChecklist(
            this.candidate.CprID,
            this.$fun.getUserID()
          ),
          HmnCandidateProcessService.promotionFiles(
            this.candidate.CprID,
            this.$fun.getUserID()
          ),
        ]);

        this.items = Array.isArray(checklistRes.data)
          ? checklistRes.data.map((item) => ({
              ...item,
              IsChecked: !!item.IsChecked,
              Observation: item.Observation || "",
            }))
          : [];
        this.files = Array.isArray(filesRes.data) ? filesRes.data : [];
        this.emitSummary();
      } catch (error) {
        this.$fun.alert(
          error?.response?.data || "No se pudo cargar el checklist de promoción",
          "error"
        );
      } finally {
        this.loadingData = false;
      }
    },
    async saveChecklist() {
      if (!this.candidate?.CprID) return;

      this.saving = true;
      try {
        await HmnCandidateProcessService.savePromotionChecklist(
          {
            CprID: this.candidate.CprID,
            Items: this.items.map((item) => ({
              DedValue: item.DedValue,
              IsChecked: !!item.IsChecked,
              Observation: item.Observation || "",
            })),
          },
          this.$fun.getUserID()
        );
        this.$fun.alert("Checklist de promoción actualizado", "success");
        this.emitSummary();
      } catch (error) {
        this.$fun.alert(
          error?.response?.data || "No se pudo guardar el checklist",
          "error"
        );
      } finally {
        this.saving = false;
      }
    },
    async uploadFiles() {
      if (!this.candidate?.CprID || !this.selectedFiles.length) return;

      this.uploadingFiles = true;
      try {
        const files = Array.isArray(this.selectedFiles)
          ? this.selectedFiles
          : [this.selectedFiles];

        for (const file of files) {
          if (!file) continue;
          const cleanName = (file.name || "adjunto")
            .replace(/\s+/g, "_")
            .replace(/[^a-zA-Z0-9_.-]/g, "");
          const serverName = `PROMO_${this.candidate.CprID}_${Date.now()}_${cleanName}`;
          const uploadRes = await GenUploadFiles.upload(
            file,
            serverName,
            "PromotionArchive__RRHH",
            true
          );
          await HmnCandidateProcessService.savePromotionFile(
            {
              CprID: this.candidate.CprID,
              FileName: uploadRes.data.fileName,
              OriginalName: file.name,
            },
            this.$fun.getUserID()
          );
        }

        this.selectedFiles = [];
        await this.reloadFiles();
        this.$fun.alert("Adjuntos registrados", "success");
      } catch (error) {
        this.$fun.alert(
          error?.response?.data || "No se pudieron subir los adjuntos",
          "error"
        );
      } finally {
        this.uploadingFiles = false;
      }
    },
    async reloadFiles() {
      const filesRes = await HmnCandidateProcessService.promotionFiles(
        this.candidate.CprID,
        this.$fun.getUserID()
      );
      this.files = Array.isArray(filesRes.data) ? filesRes.data : [];
    },
    downloadFile(file) {
      GenUploadFiles.getFile("PromotionArchive__RRHH", file.FileName);
    },
    async removeFile(file) {
      const result = await this.$fun.alert(
        `¿Eliminar ${file.OriginalName || file.FileName}?`,
        "question"
      );
      if (!result?.isConfirmed) return;

      try {
        await HmnCandidateProcessService.deletePromotionFile(
          file.CpfID,
          this.$fun.getUserID()
        );
        await this.reloadFiles();
        this.$fun.alert("Adjunto retirado", "success");
      } catch (error) {
        this.$fun.alert(
          error?.response?.data || "No se pudo eliminar el adjunto",
          "error"
        );
      }
    },
    emitSummary() {
      this.$emit("summary", {
        CprID: this.candidate?.CprID || 0,
        completed: this.completedCount,
        total: this.totalCount,
      });
    },
    closeDialog() {
      this.dialog = false;
      this.$emit("close");
    },
    formatDate(value) {
      if (!value) return "";
      return value.substring(0, 16).replace("T", " ");
    },
  },
};
</script>

<style scoped>
.pmc-card__title {
  align-items: center;
}

.pmc-list {
  display: grid;
  gap: 12px;
}

.pmc-item {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  background: #f8fafc;
}

.pmc-files {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  min-height: 180px;
}
</style>